import React, { useState, useMemo, useCallback } from "react";
import {
  Drawer,
  Typography,
  IconButton,
  Chip,
  makeStyles,
  Box,
  Badge,
  Card,
  CardContent,
  Fade,
  Grow,
  Tooltip,
} from "@material-ui/core";
import {
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  LocationOn as LocationOnIcon,
  DirectionsCar as DirectionsCarIcon,
  PowerOff as PowerOffIcon,
  Speed as SpeedIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Alarm as AlarmIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  notificationButton: {
    background: "linear-gradient(145deg, #ced1ddff 0%, #ffffffff 100%)",
    color: "white",
    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
    "&:hover": {
      background: "linear-gradient(145deg, #764ba2 0%, #667eea 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 12px 40px rgba(102, 126, 234, 0.4)",
    },
    marginRight: "12px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  badge: {
    "& .MuiBadge-badge": {
      background: "linear-gradient(45deg, #ff6b6b, #ee5a52)",
      color: "white",
      fontSize: "11px",
      fontWeight: "700",
      minWidth: "20px",
      height: "20px",
      animation: "$pulse 2s infinite",
      boxShadow: "0 2px 8px rgba(255, 107, 107, 0.3)",
      marginTop: "-10px",
    },
  },
  "@keyframes pulse": {
    "0%": {
      boxShadow: "0 0 0 0 rgba(255, 107, 107, 0.7)",
      transform: "scale(1)",
    },
    "70%": {
      boxShadow: "0 0 0 10px rgba(255, 107, 107, 0)",
      transform: "scale(1.05)",
    },
    "100%": {
      boxShadow: "0 0 0 0 rgba(255, 107, 107, 0)",
      transform: "scale(1)",
    },
  },
  drawerPaper: {
    width: 550,
    // maxHeight:"90vh",
    background: "linear-gradient(180deg, #f8f9ff 0%, #e8eeff 100%)",
    borderTopLeftRadius: "24px",
    borderBottomLeftRadius: "24px",
    boxShadow: "-10px 0 50px rgba(0, 0, 0, 0.1)",
    border: "none",
    marginTop: "75px",
  },
  header: {
    // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    background: "linear-gradient(135deg, #0077c2, #00c6ff)",
    color: "#fff",
    padding: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "-50%",
      right: "-50%",
      width: "200%",
      height: "200%",
      background:
        "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
      animation: "$shimmer 3s ease-in-out infinite",
    },
  },
  "@keyframes shimmer": {
    "0%, 100%": { transform: "rotate(0deg)" },
    "50%": { transform: "rotate(180deg)" },
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "relative",
    zIndex: 1,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
  },
  title: {
    fontWeight: "700",
    fontSize: "1.5rem",
    marginRight: theme.spacing(2),
    color: "#fff",
    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  countChip: {
    background: "linear-gradient(45deg, #fff, #f0f0f0)",
    color: "#667eea",
    fontWeight: "700",
    fontSize: "0.85rem",
    height: "32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  closeButton: {
    background: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    backdropFilter: "blur(10px)",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.3)",
      transform: "rotate(90deg)",
    },
    transition: "all 0.3s ease",
  },
  list: {
    padding: theme.spacing(2),
    maxHeight: "calc(100vh - 120px)",
    overflow: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(0,0,0,0.1)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(45deg, #667eea, #764ba2)",
      borderRadius: "10px",
    },
  },
  notificationCard: {
    marginBottom: theme.spacing(2),
    borderRadius: "16px",
    background: "linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12)",
    },
  },
  cardContent: {
    padding: theme.spacing(2.5),
    "&:last-child": {
      paddingBottom: theme.spacing(2.5),
    },
  },
  alertHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  alertIcon: {
    marginRight: theme.spacing(1.5),
    padding: theme.spacing(1),
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  alertTitle: {
    fontWeight: "700",
    fontSize: "0.9rem",
    color: "#2d3748",
    marginBottom: theme.spacing(0.5),
  },
  alertSubtitle: {
    fontSize: "0.9rem",
    color: "#718096",
    fontWeight: "500",
  },
  detailsSection: {
    marginTop: theme.spacing(2),
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    borderRadius: "8px",
    background: "rgba(102, 126, 234, 0.05)",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(102, 126, 234, 0.1)",
    },
  },
  detailIcon: {
    marginRight: theme.spacing(1.5),
    color: "#667eea",
    fontSize: "1.2rem",
  },
  detailLabel: {
    fontWeight: "600",
    color: "#4a5568",
    minWidth: "80px",
    fontSize: "0.85rem",
  },
  detailValue: {
    color: "#2d3748",
    fontSize: "0.9rem",
    fontWeight: "500",
    flex: 1,
  },
  statusChip: {
    fontWeight: "600",
    fontSize: "0.75rem",
    height: "28px",
    borderRadius: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  timestamp: {
    fontSize: "0.8rem",
    color: "#718096",
    textAlign: "right",
    marginTop: theme.spacing(1),
    fontWeight: "500",
    padding: theme.spacing(0.5, 1),
    background: "rgba(113, 128, 150, 0.1)",
    borderRadius: "6px",
    display: "inline-block",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#718096",
  },
}));

const ALERT_CONFIGS = {
  tampering: {
    icon: <SecurityIcon />,
    color: "#e53e3e",
    bgColor: "rgba(229,62,62,0.1)",
    chipColor: "secondary",
  },
  ignition: {
    icon: <DirectionsCarIcon />,
    color: "#3182ce",
    bgColor: "rgba(49,130,206,0.1)",
    chipColor: "primary",
  },
  geofenceExit: {
    icon: <LocationOnIcon />,
    color: "#d69e2e",
    bgColor: "rgba(214,158,46,0.1)",
    chipColor: "default",
  },
  overspeed: {
    icon: <SpeedIcon />,
    color: "#dd6b20",
    bgColor: "rgba(221,107,32,0.1)",
    chipColor: "default",
  },
  powerCut: {
    icon: <PowerOffIcon />,
    color: "#e53e3e",
    bgColor: "rgba(229,62,62,0.1)",
    chipColor: "secondary",
  },
  default: {
    icon: <InfoIcon />,
    color: "#667eea",
    bgColor: "rgba(102,126,234,0.1)",
    chipColor: "default",
  },
};

const formatEventTime = (time) => {
  if (!time) return "N/A";
  return new Date(time).toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// 🧠 Memoized single Notification item
const NotificationItem = React.memo(({ notification, index, classes }) => {
  const alarm = notification?.attributes?.alarm
    ? notification?.attributes?.alarm
    : notification.type;
  const alert = ALERT_CONFIGS[alarm] || ALERT_CONFIGS.default;
  // console.log(notification, alarm, "alarmhhhh");
  return (
    <Grow in timeout={300 + index * 100}>
      <Card className={classes.notificationCard}>
        <CardContent>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Chip
              label={alarm || "Unknown"}
              color={alert.chipColor}
              style={{
                borderColor: alert.color,
                color: alert.color,
                border: "1px solid",
              }}
              variant="outlined"
            />
          </div>
          <div
            style={{ display: "flex", alignItems: "center", margin: "10px 0" }}
          >
            <div
              style={{
                backgroundColor: alert.bgColor,
                color: alert.color,
                borderRadius: "10px",
                padding: 6,
                marginRight: 10,
              }}
            >
              {alert.icon}
            </div>
            <Typography style={{ fontWeight: 600, fontSize: "0.9rem" }}>
              {notification?.message || "--"}
            </Typography>
          </div>

          <div className={classes.detailRow}>
            <LocationOnIcon
              style={{ fontSize: 18, marginRight: 8, color: "#667eea" }}
            />
            <span>
              {notification?.site
                ? `${notification?.site?.name}`
                : "Address not available"}
            </span>
          </div>
          <div className={classes.detailRow}>
            <LocationOnIcon
              style={{ fontSize: 18, marginRight: 8, color: "#667eea" }}
            />
            <span>
              {notification?.position
                ? `${notification?.position?.latitude}, ${notification?.position?.longitude}`
                : "Address not available"}
            </span>
          </div>
          <div className={classes.detailRow}>
            <AlarmIcon
              style={{ fontSize: 18, marginRight: 8, color: "#667eea" }}
            />
            <span>{formatEventTime(notification?.eventTime)}</span>
          </div>
        </CardContent>
      </Card>
    </Grow>
  );
});

const NotificationDrawer = React.memo(({ events = [], setEvents }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  // console.log(events,"eventsffff")

  // ✅ useCallback to prevent re-renders
  const toggleDrawer = useCallback(
    (val) => () => {
      setOpen(val);
      if (!val) {
        setEvents([]);
      }
    },

    []
  );

  // ✅ useMemo for stable derived data (prevents unnecessary re-renders)
  const memoizedEvents = useMemo(
    () => (Array.isArray(events) ? events : []),
    [events]
  );

  return (
    <Box>
      <Tooltip title="View Notifications" arrow>
        <Badge
          badgeContent={memoizedEvents.length}
          className={classes.badge}
          max={99}
        >
          <IconButton
            onClick={toggleDrawer(true)}
            className={classes.notificationButton}
            size="small"
          >
            <NotificationsIcon style={{ fontSize: 28, color: "#003a6b" }} />
          </IconButton>
        </Badge>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={toggleDrawer(false)}
        classes={{ paper: classes.drawerPaper }}
        transitionDuration={300}
      >
        <div className={classes.header}>
          <div className={classes.headerContent}>
            <Typography variant="h6" className={classes.title}>
              Notifications
            </Typography>
            <Chip label={memoizedEvents.length} />
            <IconButton onClick={toggleDrawer(false)} style={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <div className={classes.list}>
          {memoizedEvents.length > 0 ? (
            memoizedEvents.map((n, i) => (
              <NotificationItem
                key={n.id || i}
                notification={n}
                index={i}
                classes={classes}
              />
            ))
          ) : (
            <Fade in timeout={500}>
              <Box textAlign="center" p={4} color="#718096">
                <NotificationsIcon style={{ fontSize: 48, color: "#cbd5e0" }} />
                <Typography variant="h6" style={{ marginTop: 16 }}>
                  No notifications
                </Typography>
                <Typography variant="body2" style={{ color: "#a0aec0" }}>
                  You're all caught up!
                </Typography>
              </Box>
            </Fade>
          )}
        </div>
      </Drawer>
    </Box>
  );
});

export default NotificationDrawer;
